import { type PropsWithChildren, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { ErrorPage } from '@/components/ErrorPage';
import { LoaderFull } from '@/components/LoaderFull';
import { useGetAccountQuery } from '@/features/auth';
import { useAuth } from '@/features/auth/useAuth';
import { UserType } from '@/services/api';

import { removeSecurityTokens } from './authUtils';

export const GuardAuthenticated: React.FC<
  PropsWithChildren<{
    loginPath: string;
    authorizations?: Array<UserType>;
  }>
> = ({ children, loginPath, authorizations }) => {
  const { anonymousShared, shareToken, token } = useAuth();

  const me = useGetAccountQuery({
    enabled: !anonymousShared,
  });

  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Redirect to login
  useEffect(() => {
    if (shareToken) return;
    if (pathname.includes('shared-report')) return;

    if ((me.isError && me.error.response?.status === 401) || !token) {
      removeSecurityTokens();
      navigate(`${loginPath}?redirect=${pathname}`, { replace: true });
    }
  }, [
    pathname,
    loginPath,
    me.isError,
    me.error?.response?.status,
    navigate,
    shareToken,
    token,
  ]);

  if (me.isError && me.error.response?.status !== 401) {
    return <ErrorPage errorCode={me.error.response?.status} />;
  }

  // If no requested authorizations, check the isAuthenticated
  if (!authorizations && (me.data || shareToken)) {
    return <>{children}</>;
  }

  // If no requested authorizations, check the isAuthenticated
  if (me.data?.userType || shareToken) {
    if (authorizations?.includes(UserType.SharedInterview) && shareToken) {
      return <>{children}</>;
    }

    return !authorizations ||
      authorizations.some((a) => me.data?.userType?.includes(a)) ? (
      <>{children}</>
    ) : (
      <ErrorPage errorCode={403} />
    );
  }

  return <LoaderFull />;
};
